// =============================================================================
// SIREPPAR - Página de Gestión de Padrones Electorales
// =============================================================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../services/api';
import Modal from '../../components/common/Modal';
import DataTable from '../../components/common/DataTable';

// Cada lote y el cierre de la carga superan con holgura el timeout general de 30 s
// de api.ts: mismo criterio que verificarAdherentes.
const TIMEOUT_CARGA_PADRON = 300000;

// El usuario no confirmó una reducción del padrón: se descarta la carga.
class CargaCancelada extends Error {}

type TipoPadron = 'electoral' | 'extranjeros' | 'inhabilitados';

const tiposInhabilitacion = [
  { value: 'CONDENA_PENAL', label: 'Condena Penal' },
  { value: 'INHABILITACION_JUDICIAL', label: 'Inhabilitación Judicial' },
  { value: 'FUERZAS_SEGURIDAD', label: 'Fuerzas de Seguridad' },
  { value: 'EXCLUSION_PARTIDARIA', label: 'Exclusión Partidaria' },
  { value: 'DEUDOR_ESTADO', label: 'Deudor del Estado' },
  { value: 'FUNCIONARIO_INCOMPATIBLE', label: 'Funcionario Incompatible' },
];

export default function PadronesPage() {
  const queryClient = useQueryClient();
  const [tipoPadron, setTipoPadron] = useState<TipoPadron>('electoral');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showValidarModal, setShowValidarModal] = useState(false);
  const [showConfirmRevalidar, setShowConfirmRevalidar] = useState(false);
  const [showInhabilitadoModal, setShowInhabilitadoModal] = useState(false);
  const [dniValidar, setDniValidar] = useState('');
  const [resultadoValidacion, setResultadoValidacion] = useState<any>(null);
  const [fechaPadronNuevo, setFechaPadronNuevo] = useState('');
  const [progresoCarga, setProgresoCarga] = useState<{ actual: number; total: number; cerrando?: boolean } | null>(null);
  
  // Form inhabilitado
  const [formInhabilitado, setFormInhabilitado] = useState({
    dni: '',
    apellido: '',
    nombre: '',
    sexo: '',
    tipoInhabilitacion: '',
    motivoDetalle: '',
    fechaDesde: '',
    fechaHasta: '',
    resolucionNumero: '',
    organismoEmisor: '',
    alcance: 'TOTAL',
  });

  // Query datos del padrón
  const { data, isLoading } = useQuery({
    queryKey: ['padron', tipoPadron, page, search],
    queryFn: async () => {
      const params: any = { page, pageSize: 50 };
      if (search) params.search = search;
      const response = await api.get(`/padrones/${tipoPadron}`, { params });
      return response.data;
    },
  });

  // Query estadísticas
  const { data: statsProvincial } = useQuery({
    queryKey: ['padron-stats-provincial'],
    queryFn: async () => {
      const response = await api.get('/padrones/provincial/estadisticas');
      return response.data.data;
    },
    enabled: tipoPadron === 'electoral',
  });

  // Mutation validar DNI
  const validarMutation = useMutation({
    mutationFn: async (dni: string) => {
      const response = await api.get(`/padrones/validar/${dni}`);
      return response.data.data;
    },
    onSuccess: (data) => {
      setResultadoValidacion(data);
    },
    onError: () => {
      toast.error('Error al validar DNI');
    },
  });

  // Mutation crear inhabilitado
  const crearInhabilitadoMutation = useMutation({
    mutationFn: async (data: any) => {
      return api.post('/padrones/inhabilitados', data);
    },
    onSuccess: () => {
      toast.success('Inhabilitado registrado');
      queryClient.invalidateQueries({ queryKey: ['padron', 'inhabilitados'] });
      setShowInhabilitadoModal(false);
      resetFormInhabilitado();
    },
    onError: () => {
      toast.error('Error al registrar inhabilitado');
    },
  });

  // Se llama una vez por lote — ver handleFileUpload. Para el padrón electoral el
  // lote va al staging de la carga (cargaId), no al padrón vigente.
  const cargaMasivaMutation = useMutation({
    mutationFn: async ({ tipo, registros, cargaId, lote }: { tipo: TipoPadron; registros: any[]; cargaId: string | null; lote: number }) => {
      const body = tipo === 'electoral' ? { cargaId, lote, registros } : { registros };
      return api.post(`/padrones/${tipo}/carga-masiva`, body, { timeout: TIMEOUT_CARGA_PADRON });
    },
  });

  const revalidarMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/afiliados/revalidar-padrones');
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(
        `${data.actualizados} afiliados actualizados (${data.extranjeros} extranjeros, ${data.inhabilitados} inhabilitados)`
      );
      queryClient.invalidateQueries({ queryKey: ['padron'] });
    },
    onError: () => {
      toast.error('Error al revalidar afiliados');
    }
  });  

  const resetFormInhabilitado = () => {
    setFormInhabilitado({
      dni: '',
      apellido: '',
      nombre: '',
      sexo: '',
      tipoInhabilitacion: '',
      motivoDetalle: '',
      fechaDesde: '',
      fechaHasta: '',
      resolucionNumero: '',
      organismoEmisor: '',
      alcance: 'TOTAL',
    });
  };

  const handleValidar = () => {
    if (dniValidar.length >= 7) {
      validarMutation.mutate(dniValidar);
    }
  };

  // Tiene que coincidir con PADRON_MAX_REGISTROS_POR_LOTE del backend
  // (services/padronCargaService.ts).
  const TAMANIO_LOTE_CARGA = 5000;

  // Cierra la carga: el backend reemplaza el padrón vigente en una transacción. Si
  // el padrón nuevo trae menos del 90 % de las filas del vigente, el backend pide
  // confirmación explícita (409 con requiereConfirmacion): la reducción puede ser
  // legítima, pero tiene que declararse.
  const cerrarVersion = async (cargaId: string, filasEsperadas: number) => {
    const body = { cargaId, fechaPadron: fechaPadronNuevo, filasEsperadas };
    try {
      await api.post('/padrones/electoral/cerrar-version', body, { timeout: TIMEOUT_CARGA_PADRON });
    } catch (err: any) {
      const det = err?.response?.data?.error?.details;
      if (err?.response?.status !== 409 || !det?.requiereConfirmacion) throw err;
      const ok = window.confirm(
        `El padrón nuevo trae ${det.filasNuevas.toLocaleString('es-AR')} registros y el vigente ` +
        `${det.filasVigentes.toLocaleString('es-AR')}: ${det.reduccionPorcentaje} % menos.\n\n` +
        '¿Confirmás que la reducción es correcta y querés reemplazar el padrón?',
      );
      if (!ok) throw new CargaCancelada();
      await api.post('/padrones/electoral/cerrar-version', { ...body, confirmarReduccion: true }, { timeout: TIMEOUT_CARGA_PADRON });
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (tipoPadron === 'electoral' && !fechaPadronNuevo) {
      toast.error('Ingresá la fecha del padrón antes de subir el archivo');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      let cargaId: string | null = null;
      try {
        const text = event.target?.result as string;
        const lines = text.split('\n').slice(1); // Saltar header

        const registros = lines
          .filter(line => line.trim())
          .map(line => {
            const cols = line.split(',').map(s => s.trim().replace(/^"|"$/g, ''));

            if (tipoPadron === 'electoral') {
              return {
                dni: cols[0],
                apellido: cols[1],
                nombre: cols[2],
                sexo: cols[3],
                clase: cols[4],
                fechaNacimiento: cols[5],
                seccionElectoral: cols[6],
                circuito: cols[7],
                mesa: cols[8],
                orden: cols[9],
                domicilio: cols[10],
                localidad: cols[11],
                departamento: cols[12],
                codigoPostal: cols[13],
              };
            } else if (tipoPadron === 'extranjeros') {
              return {
                dni: cols[0],
                tipoDocumento: cols[1],
                apellido: cols[2],
                nombre: cols[3],
                sexo: cols[4],
                fechaNacimiento: cols[5],
                nacionalidad: cols[6],
                seccionElectoral: cols[7],
                circuito: cols[8],
                mesa: cols[9],
                domicilio: cols[10],
                localidad: cols[11],
                departamento: cols[12],
                fechaResidencia: cols[13],
                tipoResidencia: cols[14],
              };
            } else {
              return {
                dni: cols[0],
                apellido: cols[1],
                nombre: cols[2],
                sexo: cols[3],
                fechaNacimiento: cols[4],
                tipoInhabilitacion: cols[5],
                motivoDetalle: cols[6],
                fechaDesde: cols[7],
                fechaHasta: cols[8],
                resolucionNumero: cols[9],
                organismoEmisor: cols[10],
                alcance: cols[11] || 'TOTAL',
              };
            }
          })
          .filter(r => r.dni);

        if (registros.length === 0) {
          toast.error('No se encontraron registros válidos');
          return;
        }

        const lotes: (typeof registros)[] = [];
        for (let i = 0; i < registros.length; i += TAMANIO_LOTE_CARGA) {
          lotes.push(registros.slice(i, i + TAMANIO_LOTE_CARGA));
        }

        setProgresoCarga({ actual: 0, total: lotes.length });
        let totalExitosos = 0;
        let totalErrores = 0;

        // Padrón electoral: los lotes van al staging de una carga. El padrón vigente
        // recién se reemplaza al cerrar la versión, todo de una vez.
        if (tipoPadron === 'electoral') {
          const carga = await api.post('/padrones/electoral/cargas');
          cargaId = carga.data.data.id;
        }

        for (let i = 0; i < lotes.length; i++) {
          const response = await cargaMasivaMutation.mutateAsync({ tipo: tipoPadron, registros: lotes[i], cargaId, lote: i });
          totalExitosos += response.data.data.exitosos;
          totalErrores += response.data.data.errores;
          setProgresoCarga({ actual: i + 1, total: lotes.length });
        }

        if (tipoPadron === 'electoral') {
          setProgresoCarga({ actual: lotes.length, total: lotes.length, cerrando: true });
          await cerrarVersion(cargaId as string, totalExitosos);
          cargaId = null; // cerrada: ya no hay nada que descartar
          toast.success(`Padrón reemplazado: ${totalExitosos} registros${totalErrores ? ` (${totalErrores} filas sin DNI no se cargaron)` : ''}`);
          setFechaPadronNuevo('');
        } else {
          toast.success(`Carga completada: ${totalExitosos} exitosos, ${totalErrores} errores`);
        }

        queryClient.invalidateQueries({ queryKey: ['padron'] });
        setShowUploadModal(false);
      } catch (err: any) {
        if (cargaId) {
          // Descartar el staging: el padrón vigente nunca se tocó. Si falla, la
          // próxima carga lo descarta sola al iniciarse.
          await api.delete(`/padrones/electoral/cargas/${cargaId}`).catch(() => undefined);
        }
        const mensaje = err?.response?.data?.error?.message;
        if (tipoPadron === 'electoral') {
          toast.error(err instanceof CargaCancelada
            ? 'Carga cancelada. El padrón vigente no se modificó.'
            : `${mensaje || 'Error en la carga'}. El padrón vigente no se modificó.`);
        } else {
          toast.error(mensaje || 'Error en la carga — revisá el padrón antes de reintentar');
        }
      } finally {
        setProgresoCarga(null);
      }
    };
    reader.readAsText(file);
  };

  const handleDownloadPlantilla = async () => {
    try {
      const response = await api.get(`/padrones/plantilla/${tipoPadron}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `plantilla_${tipoPadron}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Plantilla descargada');
    } catch (error) {
      toast.error('Error al descargar plantilla');
    }
  };

  // Columnas según tipo de padrón
  const getColumns = () => {
    const baseColumns = [
      { key: 'dni', header: 'DNI', width: '100px' },
      { key: 'apellido', header: 'Apellido' },
      { key: 'nombre', header: 'Nombre' },
      { key: 'sexo', header: 'Sexo', width: '60px' },
    ];

    if (tipoPadron === 'electoral') {
      return [
        { key: 'dni', header: 'DNI', width: '100px' },
        { key: 'apellido', header: 'Apellido' },
        { key: 'nombre', header: 'Nombre' },
        { key: 'genero', header: 'Sexo', width: '60px' },
        { key: 'localidad', header: 'Localidad' },
        { key: 'departamento', header: 'Departamento' },
        { key: 'seccion', header: 'Sección', width: '80px' },
        { key: 'mesa', header: 'Mesa', width: '60px' },
      ];
    } else if (tipoPadron === 'extranjeros') {
      return [
        ...baseColumns,
        { key: 'nacionalidad', header: 'Nacionalidad' },
        { key: 'localidad', header: 'Localidad' },
        { key: 'tipoResidencia', header: 'Residencia' },
      ];
    } else {
      return [
        ...baseColumns,
        {
          key: 'tipoInhabilitacion',
          header: 'Tipo',
          render: (value: string) => {
            const tipo = tiposInhabilitacion.find(t => t.value === value);
            return <span className="text-xs">{tipo?.label || value}</span>;
          },
        },
        {
          key: 'fechaDesde',
          header: 'Desde',
          render: (value: string) => value ? new Date(value).toLocaleDateString('es-AR') : '-',
        },
        {
          key: 'fechaHasta',
          header: 'Hasta',
          render: (value: string) => value ? new Date(value).toLocaleDateString('es-AR') : 'Permanente',
        },
        { key: 'alcance', header: 'Alcance', width: '80px' },
      ];
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📋 Padrones Electorales</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gestión de padrones para validación de afiliados, avales y candidatos
          </p>
        </div>
        <div className="flex gap-2 mt-4 sm:mt-0">
          <button
            onClick={() => setShowValidarModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            🔍 Validar DNI
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            📤 Cargar Datos
          </button>
          {(tipoPadron === 'extranjeros' || tipoPadron === 'inhabilitados') && (
            <button
              onClick={() => setShowConfirmRevalidar(true)}
              disabled={revalidarMutation.isPending}
              className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
            >
              {revalidarMutation.isPending ? 'Revalidando...' : 'Re-validar afiliados'}
            </button>
          )}
        </div>
      </div>
      {/* Tabs de tipo de padrón */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b">
          <nav className="flex -mb-px">
            {[
              { id: 'electoral', label: '🗳️ Padrón Electoral', color: 'blue' },
              { id: 'extranjeros', label: '🌍 Padrón Extranjeros', color: 'green' },
              { id: 'inhabilitados', label: '🚫 Inhabilitados', color: 'red' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setTipoPadron(tab.id as TipoPadron); setPage(1); setSearch(''); }}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  tipoPadron === tab.id
                    ? `border-${tab.color}-500 text-${tab.color}-600`
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Estadísticas */}
        {tipoPadron === 'electoral' && statsProvincial && (
          <div className="p-4 bg-blue-50 border-b">
            <div className="flex gap-8">
              <div>
                <span className="text-sm text-gray-600">Total Electores:</span>
                <span className="ml-2 font-bold text-blue-600">{statsProvincial.total?.toLocaleString() || 0}</span>
              </div>
              <div>
                <span className="text-sm text-gray-600">Departamentos:</span>
                <span className="ml-2 font-bold">{statsProvincial.porDepartamento?.length || 0}</span>
              </div>
            </div>
          </div>
        )}

        {/* Búsqueda y acciones */}
        <div className="p-4 flex gap-4 items-center">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Buscar por DNI, nombre o apellido..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full border rounded-lg px-4 py-2"
            />
          </div>
          {tipoPadron === 'inhabilitados' && (
            <button
              onClick={() => setShowInhabilitadoModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              + Agregar Inhabilitado
            </button>
          )}
        </div>

        {/* Tabla */}
        <DataTable
          data={data?.data || []}
          columns={getColumns()}
          loading={isLoading}
          emptyMessage={`No hay registros en el padrón de ${tipoPadron}`}
          pagination={{
            page,
            pageSize: 50,
            total: data?.meta?.totalItems || 0,
            onPageChange: setPage,
            onPageSizeChange: () => {},
          }}
        />
      </div>

      {/* Modal Validar DNI */}
      <Modal
        isOpen={showValidarModal}
        onClose={() => { setShowValidarModal(false); setResultadoValidacion(null); setDniValidar(''); }}
        title="🔍 Validar DNI en Padrones"
        size="lg"
      >
        <div className="space-y-4">
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Ingrese DNI..."
              value={dniValidar}
              onChange={(e) => setDniValidar(e.target.value.replace(/\D/g, ''))}
              maxLength={8}
              className="flex-1 border rounded-lg px-4 py-2"
            />
            <button
              onClick={handleValidar}
              disabled={dniValidar.length < 7 || validarMutation.isPending}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
            >
              {validarMutation.isPending ? 'Validando...' : 'Validar'}
            </button>
          </div>

          {resultadoValidacion && (
            <div className="border rounded-lg p-4 space-y-3">
              <h4 className="font-semibold">Resultado para DNI: {resultadoValidacion.dni}</h4>
              
              <div className="grid grid-cols-2 gap-4">
              <div className={`p-3 rounded-lg ${resultadoValidacion.enPadronElectoral ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <span className="text-lg mr-2">{resultadoValidacion.enPadronElectoral ? '✅' : '❌'}</span>
                  Padrón Electoral
                </div>
                <div className={`p-3 rounded-lg ${resultadoValidacion.enPadronExtranjeros ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <span className="text-lg mr-2">{resultadoValidacion.enPadronExtranjeros ? '✅' : '❌'}</span>
                  Padrón Extranjeros
                </div>
              </div>

              {resultadoValidacion.inhabilitado && (
                <div className="bg-red-100 border border-red-300 rounded-lg p-3">
                  <p className="font-semibold text-red-800">⚠️ INHABILITADO</p>
                  <p className="text-sm text-red-700">
                    Tipo: {resultadoValidacion.inhabilitacion?.tipoInhabilitacion}
                  </p>
                  {resultadoValidacion.inhabilitacion?.motivoDetalle && (
                    <p className="text-sm text-red-700">
                      Motivo: {resultadoValidacion.inhabilitacion.motivoDetalle}
                    </p>
                  )}
                </div>
              )}

              {resultadoValidacion.datosPersona && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600">Datos encontrados:</p>
                  <p className="font-medium">
                    {resultadoValidacion.datosPersona.apellido}, {resultadoValidacion.datosPersona.nombre}
                  </p>
                </div>
              )}

              <div className="border-t pt-3 mt-3">
                <p className="text-sm font-medium mb-2">Habilitaciones:</p>
                <div className="flex gap-4">
                  <span className={resultadoValidacion.puedeAfiliarse ? 'text-green-600' : 'text-red-600'}>
                    {resultadoValidacion.puedeAfiliarse ? '✅' : '❌'} Puede afiliarse
                  </span>
                  <span className={resultadoValidacion.puedeSerAval ? 'text-green-600' : 'text-red-600'}>
                    {resultadoValidacion.puedeSerAval ? '✅' : '❌'} Puede ser aval
                  </span>
                  <span className={resultadoValidacion.puedeSerCandidato ? 'text-green-600' : 'text-red-600'}>
                    {resultadoValidacion.puedeSerCandidato ? '✅' : '❌'} Puede ser candidato
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Modal Carga Masiva */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        title={`📤 Cargar Padrón ${tipoPadron.charAt(0).toUpperCase() + tipoPadron.slice(1)}`}
        showCloseButton={!progresoCarga}
        closeOnOverlayClick={!progresoCarga}
      >
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
            <p className="font-medium text-blue-800">Instrucciones:</p>
            <ol className="list-decimal ml-4 text-blue-700 mt-1">
              <li>Descarga la plantilla CSV</li>
              <li>Completa los datos</li>
              <li>Sube el archivo</li>
            </ol>
          </div>

          <button
            onClick={handleDownloadPlantilla}
            disabled={!!progresoCarga}
            className="w-full px-4 py-2 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 disabled:opacity-50"
          >
            📥 Descargar Plantilla CSV
          </button>

          {tipoPadron === 'electoral' && (
            <div>
              <label className="block text-sm font-medium mb-1">Fecha que declara este padrón *</label>
              <input
                type="date"
                value={fechaPadronNuevo}
                onChange={(e) => setFechaPadronNuevo(e.target.value)}
                disabled={!!progresoCarga}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
              />
              <p className="text-xs text-gray-500 mt-1">
                La fecha que dice el padrón (ej. "actualizado al..."), no la fecha de hoy.
              </p>
            </div>
          )}

          {progresoCarga ? (
            <div className="border-2 border-primary-300 bg-primary-50 rounded-lg p-6 text-center">
              <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-3"></div>
              <p className="text-primary-800 font-medium">
                {progresoCarga.cerrando
                  ? 'Reemplazando el padrón vigente...'
                  : `Subiendo lote ${progresoCarga.actual} de ${progresoCarga.total}...`}
              </p>
              <p className="text-xs text-primary-600 mt-1">No cierres ni recargues esta ventana</p>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="padron-upload"
                disabled={tipoPadron === 'electoral' && !fechaPadronNuevo}
              />
              <label
                htmlFor="padron-upload"
                className={tipoPadron === 'electoral' && !fechaPadronNuevo ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
              >
                <div className="text-4xl mb-2">📄</div>
                <p className="text-gray-600">Click para seleccionar archivo CSV</p>
              </label>
            </div>
          )}
        </div>
      </Modal>

      {/* Modal Agregar Inhabilitado */}
      <Modal
        isOpen={showInhabilitadoModal}
        onClose={() => { setShowInhabilitadoModal(false); resetFormInhabilitado(); }}
        title="🚫 Agregar Inhabilitado"
        size="lg"
      >
        <form onSubmit={(e) => { e.preventDefault(); crearInhabilitadoMutation.mutate(formInhabilitado); }} className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">DNI *</label>
              <input
                type="text"
                value={formInhabilitado.dni}
                onChange={(e) => setFormInhabilitado({ ...formInhabilitado, dni: e.target.value })}
                required
                maxLength={8}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Apellido *</label>
              <input
                type="text"
                value={formInhabilitado.apellido}
                onChange={(e) => setFormInhabilitado({ ...formInhabilitado, apellido: e.target.value })}
                required
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nombre *</label>
              <input
                type="text"
                value={formInhabilitado.nombre}
                onChange={(e) => setFormInhabilitado({ ...formInhabilitado, nombre: e.target.value })}
                required
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Tipo de Inhabilitación *</label>
              <select
                value={formInhabilitado.tipoInhabilitacion}
                onChange={(e) => setFormInhabilitado({ ...formInhabilitado, tipoInhabilitacion: e.target.value })}
                required
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="">Seleccionar...</option>
                {tiposInhabilitacion.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Alcance</label>
              <select
                value={formInhabilitado.alcance}
                onChange={(e) => setFormInhabilitado({ ...formInhabilitado, alcance: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="TOTAL">Total</option>
                <option value="AFILIACION">Solo Afiliación</option>
                <option value="CANDIDATURA">Solo Candidatura</option>
                <option value="AVAL">Solo Aval</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Motivo / Detalle</label>
            <textarea
              value={formInhabilitado.motivoDetalle}
              onChange={(e) => setFormInhabilitado({ ...formInhabilitado, motivoDetalle: e.target.value })}
              rows={2}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Fecha Desde *</label>
              <input
                type="date"
                value={formInhabilitado.fechaDesde}
                onChange={(e) => setFormInhabilitado({ ...formInhabilitado, fechaDesde: e.target.value })}
                required
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Fecha Hasta (vacío = permanente)</label>
              <input
                type="date"
                value={formInhabilitado.fechaHasta}
                onChange={(e) => setFormInhabilitado({ ...formInhabilitado, fechaHasta: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nº Resolución</label>
              <input
                type="text"
                value={formInhabilitado.resolucionNumero}
                onChange={(e) => setFormInhabilitado({ ...formInhabilitado, resolucionNumero: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Organismo Emisor</label>
              <input
                type="text"
                value={formInhabilitado.organismoEmisor}
                onChange={(e) => setFormInhabilitado({ ...formInhabilitado, organismoEmisor: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => { setShowInhabilitadoModal(false); resetFormInhabilitado(); }}
              className="px-4 py-2 border rounded-lg"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={crearInhabilitadoMutation.isPending}
              className="px-4 py-2 bg-red-600 text-white rounded-lg disabled:opacity-50"
            >
              {crearInhabilitadoMutation.isPending ? 'Guardando...' : 'Registrar Inhabilitado'}
            </button>
          </div>
        </form>
        </Modal>

<Modal
  isOpen={showConfirmRevalidar}
  onClose={() => setShowConfirmRevalidar(false)}
  title="Re-validar afiliados"
>
  <div className="space-y-4">
    <p className="text-gray-700">
      Esto re-chequea <strong>todos los afiliados</strong> (módulos Afiliados y Control) contra los padrones vigentes de extranjeros e inhabilitados. Los estados que cambien quedarán registrados en el historial.
    </p>
    <p className="text-sm text-gray-500">¿Confirmar la ejecución?</p>
    <div className="flex justify-end gap-3">
      <button
        onClick={() => setShowConfirmRevalidar(false)}
        className="px-4 py-2 border rounded-lg"
      >
        Cancelar
      </button>
      <button
        onClick={() => { setShowConfirmRevalidar(false); revalidarMutation.mutate(); }}
        className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
      >
        Confirmar
      </button>
    </div>
  </div>
</Modal>
</div>
);
}

